import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import {
  Badge,
  Button,
  Drawer,
  Empty,
  Input,
  Select,
  Skeleton,
  Table,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { captureAnalytics } from '../../../actions/preload';
import csrfHeaders from '../../../utils/csrfHeaders';

import './manage-insurance-payer-memory.scss';

const candidateKey = (candidate) => [
  candidate.ema_payer_internal_id || '',
  candidate.external_payer_id || '',
  candidate.external_payer_code || '',
  candidate.name || '',
].join('|');

const normalizedCandidateName = (candidate) => (candidate?.name || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .replace(/\s+/g, ' ');

const emaCandidateCode = (candidate) => (
  candidate?.external_payer_code || candidate?.external_payer_id || ''
).toString().trim().toLowerCase();

const matchesPreferredCandidate = (candidate, preferredCandidate) => {
  if (preferredCandidate?.ema_payer_internal_id) {
    return candidateKey(candidate) === candidateKey(preferredCandidate);
  }

  return normalizedCandidateName(candidate) === normalizedCandidateName(preferredCandidate)
    && emaCandidateCode(candidate) === emaCandidateCode(preferredCandidate);
};

const payerDateFormatter = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

const formatDate = (value) => {
  if (!value) return 'Just now';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Just now' : payerDateFormatter.format(date);
};

const occurrenceLabel = (count) => `${count} ${count === 1 ? 'time' : 'times'}`;

const credentialLabel = (mapping) => mapping.credential_label;

const ManageInsurancePayerMemory = () => {
  const [data, setData] = useState({
    unresolved: [], resolved: [], counts: {}, pagination: {}, page_size: 25,
  });
  const [loading, setLoading] = useState(true);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candidateValue, setCandidateValue] = useState();
  const [resolvedName, setResolvedName] = useState('');
  const [externalPayerId, setExternalPayerId] = useState('');
  const [externalPayerCode, setExternalPayerCode] = useState('');
  const emaPayerInternalId = useRef(null);
  const [search, setSearch] = useState('');
  const [unresolvedPage, setUnresolvedPage] = useState(1);
  const [resolvedPage, setResolvedPage] = useState(1);
  const candidateSearchTimer = useRef(null);
  const mappingSearchTimer = useRef(null);
  const mappingRequestId = useRef(0);
  const candidateRequestId = useRef(0);
  const activeMappingId = useRef(null);

  const loadMappings = async (nextUnresolvedPage = 1, nextResolvedPage = 1, query = '') => {
    const requestId = mappingRequestId.current + 1;
    mappingRequestId.current = requestId;
    setLoading(true);
    try {
      const response = await axios.get('/api/v1/insurance_payer_mappings', {
        params: {
          unresolved_page: nextUnresolvedPage,
          resolved_page: nextResolvedPage,
          ...(query.trim() ? { query: query.trim() } : {}),
        },
      });
      if (mappingRequestId.current === requestId) setData(response.data);
    } catch {
      if (mappingRequestId.current === requestId) message.error('Payer memory could not be loaded');
    } finally {
      if (mappingRequestId.current === requestId) setLoading(false);
    }
  };

  useEffect(() => {
    loadMappings();
    return () => {
      mappingRequestId.current += 1;
      candidateRequestId.current += 1;
      activeMappingId.current = null;
      clearTimeout(candidateSearchTimer.current);
      clearTimeout(mappingSearchTimer.current);
    };
  }, []);

  const applyCandidate = (candidate) => {
    if (!candidate) return;
    setCandidateValue(candidateKey(candidate));
    setResolvedName(candidate.name || '');
    setExternalPayerId((candidate.external_payer_id || '').toString());
    setExternalPayerCode((candidate.external_payer_code || '').toString());
    emaPayerInternalId.current = candidate.ema_payer_internal_id || null;
  };

  const openMapping = async (mapping, preferredCandidate = null) => {
    const requestId = candidateRequestId.current + 1;
    candidateRequestId.current = requestId;
    activeMappingId.current = mapping.id;
    setSelectedMapping(mapping);
    setCandidates(mapping.candidates || []);
    setCandidateValue(undefined);
    setResolvedName(mapping.resolved_name || '');
    setExternalPayerId((mapping.external_payer_id || '').toString());
    setExternalPayerCode((mapping.external_payer_code || '').toString());
    emaPayerInternalId.current = mapping.ema_payer_internal_id || null;

    captureAnalytics('payer_memory_mapping_opened', {
      ehr: mapping.ehr,
      status: mapping.status,
    });

    try {
      const candidateUrl = `/api/v1/insurance_payer_mappings/${mapping.id}/candidates`;
      const response = await axios.get(
        candidateUrl,
        preferredCandidate?.name ? { params: { query: preferredCandidate.name } } : undefined,
      );
      if (activeMappingId.current !== mapping.id || candidateRequestId.current !== requestId) return;

      const nextCandidates = response.data.candidates || [];
      setCandidates(nextCandidates);
      if (preferredCandidate) {
        const freshCandidate = nextCandidates.find(
          (candidate) => candidate.provenance === 'live'
            && matchesPreferredCandidate(candidate, preferredCandidate),
        );
        applyCandidate(freshCandidate);
      }
    } catch {
      // Keep the stored snapshot available for manual selection, but never
      // preselect it when the current EHR catalog could not confirm it.
    }
  };

  const closeMapping = () => {
    candidateRequestId.current += 1;
    activeMappingId.current = null;
    clearTimeout(candidateSearchTimer.current);
    setSelectedMapping(null);
    setCandidates([]);
    setCandidateValue(undefined);
    emaPayerInternalId.current = null;
    setCandidateLoading(false);
  };

  const handleCandidateChange = (value) => {
    applyCandidate(candidates.find((candidate) => candidateKey(candidate) === value));
  };

  const searchCandidates = (query) => {
    clearTimeout(candidateSearchTimer.current);
    const mappingId = selectedMapping?.id;
    const requestId = candidateRequestId.current + 1;
    candidateRequestId.current = requestId;
    candidateSearchTimer.current = setTimeout(async () => {
      if (
        !mappingId
        || activeMappingId.current !== mappingId
        || candidateRequestId.current !== requestId
      ) return;

      setCandidateLoading(true);
      try {
        const response = await axios.get(
          `/api/v1/insurance_payer_mappings/${mappingId}/candidates`,
          query.trim() ? { params: { query: query.trim() } } : undefined,
        );
        if (activeMappingId.current === mappingId && candidateRequestId.current === requestId) {
          setCandidates(response.data.candidates || []);
        }
      } catch {
        if (activeMappingId.current === mappingId && candidateRequestId.current === requestId) {
          message.error('Carrier search could not be loaded');
        }
      } finally {
        if (activeMappingId.current === mappingId && candidateRequestId.current === requestId) {
          setCandidateLoading(false);
        }
      }
    }, 250);
  };

  const saveMapping = async () => {
    const mappingToSave = selectedMapping;
    const nameToSave = resolvedName.trim();
    const payerIdToSave = externalPayerId.trim();
    const payerCodeToSave = externalPayerCode.trim();
    const advancedmdWithoutId = mappingToSave.ehr.toLowerCase() === 'advancedmd' && !payerIdToSave;
    const missingTarget = !payerIdToSave && !payerCodeToSave;
    if (!nameToSave || missingTarget || advancedmdWithoutId) {
      message.warning(advancedmdWithoutId ? 'Enter the AdvancedMD carrier ID' : 'Choose a carrier or enter its EHR ID or code');
      return;
    }

    setSaving(true);
    try {
      await axios.patch(`/api/v1/insurance_payer_mappings/${mappingToSave.id}`, {
        insurance_payer_mapping: {
          resolved_name: nameToSave,
          external_payer_id: payerIdToSave,
          external_payer_code: payerCodeToSave,
          ...(mappingToSave.ehr.toLowerCase() === 'ema'
            ? { ema_payer_internal_id: emaPayerInternalId.current }
            : {}),
        },
      }, { headers: csrfHeaders() });
      captureAnalytics('payer_memory_mapping_saved', {
        ehr: mappingToSave.ehr,
        had_suggestion: Boolean(candidateValue),
        previous_status: mappingToSave.status,
      });
      message.success(`Future “${mappingToSave.inbound_name}” uploads will use ${nameToSave}`);
      if (activeMappingId.current === mappingToSave.id) closeMapping();
      setUnresolvedPage(1);
      setResolvedPage(1);
      await loadMappings(1, 1, search);
    } catch (error) {
      message.error(error?.response?.data?.error || 'Payer mapping could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const unresolvedColumns = [
    {
      title: 'Incoming payer',
      key: 'incoming',
      render: (_, mapping) => (
        <div className="payer-memory__incoming">
          <span className="payer-memory__payer-name">{mapping.inbound_name}</span>
          {credentialLabel(mapping) && <span className="payer-memory__credential">{credentialLabel(mapping)}</span>}
          <span className="payer-memory__sighting">
            <ClockCircleOutlined />
            {`${occurrenceLabel(mapping.occurrence_count)} · last seen ${formatDate(mapping.last_seen_at)}`}
          </span>
        </div>
      ),
    },
    {
      title: 'Why it needs attention',
      key: 'reason',
      render: (_, mapping) => {
        const candidateCount = mapping.candidates?.length || 0;
        return (
          <span className="payer-memory__exception-reason">
            {candidateCount
              ? `${candidateCount} possible ${candidateCount === 1 ? 'match' : 'matches'}`
              : 'No confident match found'}
          </span>
        );
      },
    },
    {
      title: '',
      key: 'action',
      width: 120,
      align: 'right',
      render: (_, mapping) => (
        <Button
          className="payer-memory__exception-button"
          onClick={() => openMapping(mapping, mapping.candidates?.length === 1 ? mapping.candidates[0] : null)}
        >
          Choose payer
        </Button>
      ),
    },
  ];

  const resolvedColumns = [
    {
      title: 'When Medsender sees',
      key: 'inbound_name',
      render: (_, mapping) => (
        <div className="payer-memory__incoming">
          <span className="payer-memory__payer-name">{mapping.inbound_name}</span>
          {credentialLabel(mapping) && <span className="payer-memory__credential">{credentialLabel(mapping)}</span>}
        </div>
      ),
    },
    {
      title: 'Medsender uses',
      key: 'resolved_name',
      render: (_, mapping) => (
        <div className="payer-memory__resolved-target">
          <CheckCircleOutlined />
          <div>
            <strong>{mapping.resolved_name}</strong>
            <small>{mapping.external_payer_code || mapping.external_payer_id}</small>
          </div>
        </div>
      ),
    },
    {
      title: 'How it was mapped',
      key: 'source',
      width: 150,
      render: (_, mapping) => (
        <span className="payer-memory__source">
          {mapping.source === 'manual' ? 'Changed by admin' : 'Auto-mapped'}
        </span>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 72,
      align: 'right',
      render: (_, mapping) => (
        <Button
          className="payer-memory__change-button"
          aria-label={`Change ${mapping.inbound_name}`}
          onClick={() => openMapping(mapping)}
        >
          Change
        </Button>
      ),
    },
  ];

  if (loading) {
    return <div className="payer-memory"><Skeleton active paragraph={{ rows: 7 }} /></div>;
  }

  return (
    <div className="payer-memory">
      <header className="payer-memory__header">
        <div>
          <h2>Insurance payers</h2>
          <p>Medsender uses confident matches automatically and remembers them for every future document.</p>
        </div>
        <div className="payer-memory__stats" aria-label="Payer memory summary">
          <div className="payer-memory__stat payer-memory__stat--attention">
            <strong>{data.counts?.unresolved || 0}</strong>
            <span>need attention</span>
          </div>
          <div className="payer-memory__stat payer-memory__stat--learned">
            <strong>{data.counts?.resolved || 0}</strong>
            <span>mapped</span>
          </div>
        </div>
      </header>

      <section className="payer-memory__section payer-memory__section--queue">
        <div className="payer-memory__section-heading">
          <div>
            <h3>
              <span>Needs attention</span>
              <Badge count={data.counts?.unresolved || 0} showZero />
            </h3>
            <p>Only payer names Medsender could not match confidently appear here.</p>
          </div>
        </div>
        {(data.unresolved || []).length ? (
          <Table
            rowKey="id"
            columns={unresolvedColumns}
            dataSource={data.unresolved}
            pagination={(data.pagination?.unresolved_total || 0) > data.page_size ? {
              current: unresolvedPage,
              pageSize: data.page_size,
              showSizeChanger: false,
              total: data.pagination.unresolved_total,
              onChange: (page) => {
                setUnresolvedPage(page);
                loadMappings(page, resolvedPage, search);
              },
            } : false}
            size="middle"
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No exceptions — Medsender is automatically mapping every payer seen so far."
          />
        )}
      </section>

      <section className="payer-memory__section">
        <div className="payer-memory__section-heading payer-memory__section-heading--search">
          <div>
            <h3>Automatic mappings</h3>
            <p>These matches are already being used. Change one only if Medsender chose incorrectly.</p>
          </div>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            value={search}
            placeholder="Search remembered payers"
            onChange={(event) => {
              const query = event.target.value;
              setSearch(query);
              clearTimeout(mappingSearchTimer.current);
              mappingSearchTimer.current = setTimeout(() => {
                setResolvedPage(1);
                loadMappings(unresolvedPage, 1, query);
              }, 250);
              captureAnalytics('payer_memory_mappings_searched', { has_query: Boolean(query) });
            }}
          />
        </div>
        <Table
          rowKey="id"
          columns={resolvedColumns}
          dataSource={data.resolved}
          pagination={(data.pagination?.resolved_total || 0) > data.page_size ? {
            current: resolvedPage,
            pageSize: data.page_size,
            showSizeChanger: false,
            total: data.pagination.resolved_total,
            onChange: (page) => {
              setResolvedPage(page);
              loadMappings(unresolvedPage, page, search);
            },
          } : false}
          size="middle"
          locale={{ emptyText: 'Automatic mappings appear here as Medsender learns them.' }}
        />
      </section>

      <Drawer
        width={460}
        visible={Boolean(selectedMapping)}
        onClose={closeMapping}
        title={selectedMapping?.status === 'resolved' ? 'Change payer mapping' : 'Choose a payer'}
        destroyOnClose
      >
        {selectedMapping && (
          <div className="payer-memory__drawer">
            <div className="payer-memory__drawer-source">
              <span>WHEN A DOCUMENT SAYS</span>
              <strong>{selectedMapping.inbound_name}</strong>
              {credentialLabel(selectedMapping) && <small>{credentialLabel(selectedMapping)}</small>}
              <small>{occurrenceLabel(selectedMapping.occurrence_count)}</small>
            </div>

            <div className="payer-memory__field">
              <span className="payer-memory__label">Use this EHR carrier</span>
              <Select
                id="payer-candidate"
                data-testid="payer-candidate-select"
                showSearch
                value={candidateValue}
                loading={candidateLoading}
                placeholder="Search or choose a carrier"
                optionFilterProp="label"
                filterOption={false}
                onChange={handleCandidateChange}
                onSearch={searchCandidates}
                options={candidates.map((candidate) => ({
                  value: candidateKey(candidate),
                  label: `${candidate.name}${candidate.external_payer_code ? ` (${candidate.external_payer_code})` : ''}`,
                }))}
              />
              {!candidates.length && (
                <small>No suggestions were returned. Enter the carrier details below.</small>
              )}
            </div>

            <div className="payer-memory__manual-fields">
              <div className="payer-memory__field">
                <span className="payer-memory__label">Carrier name</span>
                <Input
                  id="payer-name"
                  value={resolvedName}
                  onChange={(event) => {
                    setResolvedName(event.target.value);
                    setCandidateValue(undefined);
                    emaPayerInternalId.current = null;
                  }}
                />
              </div>
              <div className="payer-memory__field-row">
                <div className="payer-memory__field">
                  <span className="payer-memory__label">EHR ID</span>
                  <Input
                    id="payer-id"
                    value={externalPayerId}
                    onChange={(event) => {
                      setExternalPayerId(event.target.value);
                      setCandidateValue(undefined);
                      emaPayerInternalId.current = null;
                    }}
                  />
                </div>
                <div className="payer-memory__field">
                  <span className="payer-memory__label">Code</span>
                  <Input
                    id="payer-code"
                    value={externalPayerCode}
                    onChange={(event) => {
                      setExternalPayerCode(event.target.value);
                      setCandidateValue(undefined);
                      emaPayerInternalId.current = null;
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="payer-memory__promise">
              <CheckCircleOutlined />
              <span>This choice will be used automatically for every future document with this payer name.</span>
            </div>

            <div className="payer-memory__drawer-actions">
              <Button onClick={closeMapping}>Cancel</Button>
              <Button type="primary" loading={saving} onClick={saveMapping}>
                {selectedMapping.status === 'resolved' ? 'Save change' : 'Use payer'}
              </Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ManageInsurancePayerMemory;
